/**
 * Audit Timeline Component
 * Visual timeline view for audit trail entries
 */

import React from 'react';
import { 
  Plus, Edit, Trash2, CheckCircle, XCircle, RefreshCw, 
  FileText, Send, User, Clock, Calendar, MapPin 
} from 'lucide-react';

// Map action types to icons and colors
const ACTION_CONFIG = {
  'create': { icon: Plus, color: 'green', label: 'Created' },
  'update': { icon: Edit, color: 'blue', label: 'Updated' },
  'delete': { icon: Trash2, color: 'red', label: 'Deleted' },
  'approve': { icon: CheckCircle, color: 'green', label: 'Approved' },
  'reject': { icon: XCircle, color: 'red', label: 'Rejected' },
  'reopen': { icon: RefreshCw, color: 'orange', label: 'Reopened' },
  'submit': { icon: Send, color: 'indigo', label: 'Submitted' },
  'calculate': { icon: FileText, color: 'purple', label: 'Calculated' }
};

const getActionConfig = (action) => {
  const key = action.toLowerCase();
  return ACTION_CONFIG[key] || { icon: FileText, color: 'slate', label: action };
};

const getRelativeTime = (timestamp) => {
  const now = new Date();
  const past = new Date(timestamp);
  const diffMs = now - past;
  const diffMins = Math.floor(diffMs / 60000);
  const diffHours = Math.floor(diffMs / 3600000);
  const diffDays = Math.floor(diffMs / 86400000);

  if (diffMins < 1) return 'Just now';
  if (diffMins < 60) return `${diffMins} minute${diffMins > 1 ? 's' : ''} ago`;
  if (diffHours < 24) return `${diffHours} hour${diffHours > 1 ? 's' : ''} ago`;
  if (diffDays < 7) return `${diffDays} day${diffDays > 1 ? 's' : ''} ago`;
  
  return past.toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' });
};

const getUserInitials = (name) => {
  if (!name) return 'U';
  const parts = name.split(' ');
  if (parts.length >= 2) {
    return (parts[0][0] + parts[parts.length - 1][0]).toUpperCase();
  }
  return name.substring(0, 2).toUpperCase();
};

export default function AuditTimeline({ entries, showEntityInfo = true }) {
  if (!entries || entries.length === 0) {
    return (
      <div className="text-center py-12">
        <Clock className="w-12 h-12 text-slate-300 mx-auto mb-3" />
        <p className="text-slate-600">No audit history available</p>
      </div>
    );
  }

  return (
    <div className="relative">
      {/* Timeline line */}
      <div className="absolute left-8 top-0 bottom-0 w-0.5 bg-slate-200"></div>

      {/* Timeline entries */}
      <div className="space-y-6">
        {entries.map((entry, index) => {
          const config = getActionConfig(entry.action);
          const Icon = config.icon;
          const isLast = index === entries.length - 1;
          
          // Safe color mapping for Tailwind
          const colorClasses = {
            green: { bg: 'bg-green-100', text: 'text-green-600', badge: 'bg-green-100 text-green-700' },
            blue: { bg: 'bg-blue-100', text: 'text-blue-600', badge: 'bg-blue-100 text-blue-700' },
            red: { bg: 'bg-red-100', text: 'text-red-600', badge: 'bg-red-100 text-red-700' },
            orange: { bg: 'bg-orange-100', text: 'text-orange-600', badge: 'bg-orange-100 text-orange-700' },
            indigo: { bg: 'bg-indigo-100', text: 'text-indigo-600', badge: 'bg-indigo-100 text-indigo-700' },
            purple: { bg: 'bg-purple-100', text: 'text-purple-600', badge: 'bg-purple-100 text-purple-700' },
            slate: { bg: 'bg-slate-100', text: 'text-slate-600', badge: 'bg-slate-100 text-slate-700' }
          };
          const colors = colorClasses[config.color] || colorClasses.slate;

          return (
            <div key={entry.id || index} className="relative pl-20">
              {/* Timeline dot with icon */}
              <div className={`absolute left-0 top-0 w-16 h-16 rounded-full ${colors.bg} border-4 border-white shadow-md flex items-center justify-center`}>
                <Icon className={`w-7 h-7 ${colors.text}`} />
              </div>

              {/* Content card */}
              <div className={`bg-white rounded-lg border-2 border-slate-200 shadow-sm hover:shadow-md transition-shadow ${!isLast ? 'mb-6' : ''}`}>
                <div className="p-4">
                  {/* Header */}
                  <div className="flex items-start justify-between mb-3">
                    <div className="flex items-center gap-3">
                      <span className={`inline-flex items-center gap-1.5 px-3 py-1 rounded-full text-xs font-semibold ${colors.badge}`}>
                        <Icon className="w-3.5 h-3.5" />
                        {config.label}
                      </span>
                      {entry.entity_type && showEntityInfo && (
                        <span className="text-xs text-slate-500 px-2 py-1 bg-slate-100 rounded">
                          {entry.entity_type}
                        </span>
                      )}
                    </div>
                    <span className="text-xs text-slate-500" title={new Date(entry.timestamp).toLocaleString()}>
                      {getRelativeTime(entry.timestamp)}
                    </span>
                  </div>

                  {/* Description */}
                  <p className="text-sm text-slate-700 mb-3">
                    {entry.description || `${config.label} ${entry.entity_type || 'item'}`}
                  </p>

                  {/* User info */}
                  <div className="flex items-center gap-3 text-xs text-slate-600">
                    <div className="flex items-center gap-2">
                      <div className="w-6 h-6 rounded-full bg-indigo-100 flex items-center justify-center">
                        <span className="text-xs font-semibold text-indigo-700">
                          {getUserInitials(entry.user_email || entry.user_name)}
                        </span>
                      </div>
                      <span>{entry.user_email || entry.user_name || 'Unknown User'}</span>
                    </div>
                    {entry.user_role && (
                      <span className="px-2 py-0.5 bg-slate-100 rounded text-slate-600">
                        {entry.user_role}
                      </span>
                    )}
                  </div>

                  {/* Additional details */}
                  {entry.details && Object.keys(entry.details).length > 0 && (
                    <details className="mt-3 pt-3 border-t border-slate-200">
                      <summary className="text-xs text-slate-600 cursor-pointer hover:text-slate-900 font-medium">
                        View Details
                      </summary>
                      <div className="mt-2 p-3 bg-slate-50 rounded text-xs space-y-1">
                        {Object.entries(entry.details).map(([key, value]) => (
                          <div key={key} className="flex gap-2">
                            <span className="font-medium text-slate-600 min-w-24">{key}:</span>
                            <span className="text-slate-800">{JSON.stringify(value)}</span>
                          </div>
                        ))}
                      </div>
                    </details>
                  )}

                  {/* Changes (before/after) */}
                  {entry.changes && (
                    <div className="mt-3 pt-3 border-t border-slate-200">
                      <p className="text-xs font-semibold text-slate-600 mb-2">Changes:</p>
                      <div className="space-y-2">
                        {Object.entries(entry.changes).map(([field, change]) => (
                          <div key={field} className="text-xs bg-slate-50 rounded p-2">
                            <span className="font-medium text-slate-700">{field}:</span>
                            <div className="flex items-center gap-2 mt-1">
                              <span className="px-2 py-1 bg-red-100 text-red-700 rounded line-through">
                                {String(change.old)}
                              </span>
                              <span className="text-slate-400">→</span>
                              <span className="px-2 py-1 bg-green-100 text-green-700 rounded">
                                {String(change.new)}
                              </span>
                            </div>
                          </div>
                        ))}
                      </div>
                    </div>
                  )}
                </div>
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
}
